import React, { useState, useRef, useEffect } from 'react';
import { FingerType } from '@/types';

// Finger type categories for selection
type FingerTypeCategory = 'pinky' | 'ring' | 'middle' | 'pointing' | 'thumb';

const FINGER_TYPE_OPTIONS: Array<{
  type: FingerTypeCategory;
  label: string;
  abbreviation: string;
}> = [
    { type: 'pinky', label: 'Pinky (Pi)', abbreviation: 'Pi' },
    { type: 'ring', label: 'Ring (R)', abbreviation: 'R' },
    { type: 'middle', label: 'Middle (M)', abbreviation: 'M' },
    { type: 'pointing', label: 'Pointing (Po)', abbreviation: 'Po' },
    { type: 'thumb', label: 'Big/Thumb (B)', abbreviation: 'B' }
  ];

interface FingerToggleProps {
  selectedFingers: FingerType[];
  onChange: (fingers: FingerType[]) => void;
  className?: string;
  disabled?: boolean;
}

export const FingerToggle: React.FC<FingerToggleProps> = ({
  selectedFingers,
  onChange,
  className = '',
  disabled = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get unique finger types from selected fingers
  const getSelectedFingerTypes = (): FingerTypeCategory[] => {
    const types = new Set<FingerTypeCategory>();
    selectedFingers.forEach(finger => {
      if (finger.includes('Pinky')) types.add('pinky');
      else if (finger.includes('Ring')) types.add('ring');
      else if (finger.includes('Middle')) types.add('middle');
      else if (finger.includes('Pointing')) types.add('pointing');
      else if (finger.includes('Thumb')) types.add('thumb');
    });
    return Array.from(types);
  };

  // Convert finger types to actual finger configuration
  const fingerTypesToFingers = (types: FingerTypeCategory[]): FingerType[] => {
    const fingers: FingerType[] = [];
    types.forEach(type => {
      switch (type) {
        case 'pinky':
          fingers.push('leftPinky', 'rightPinky');
          break;
        case 'ring':
          fingers.push('leftRing', 'rightRing');
          break;
        case 'middle':
          fingers.push('leftMiddle', 'rightMiddle');
          break;
        case 'pointing':
          fingers.push('leftPointing', 'rightPointing');
          break;
        case 'thumb':
          fingers.push('leftThumb', 'rightThumb');
          break;
      }
    });
    return fingers;
  };

  // Get display text for toggle button
  const getDisplayText = (): string => {
    const selectedTypes = getSelectedFingerTypes();
    if (selectedTypes.length === 0) return '0';
    if (selectedTypes.length === 5) return 'All';

    return selectedTypes
      .map(type => FINGER_TYPE_OPTIONS.find(opt => opt.type === type)?.abbreviation || '')
      .join(',');
  };

  // Handle finger type selection
  const handleFingerTypeToggle = (type: FingerTypeCategory) => {
    const currentTypes = getSelectedFingerTypes();
    let newTypes: FingerTypeCategory[];

    if (currentTypes.includes(type)) {
      newTypes = currentTypes.filter(t => t !== type);
    } else {
      newTypes = [...currentTypes, type];
    }

    // Ensure at least one finger type is selected
    if (newTypes.length === 0) {
      newTypes = ['pinky']; // Default to pinky
    }

    const newFingers = fingerTypesToFingers(newTypes);
    onChange(newFingers);
  };

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle toggle button click
  const handleToggleClick = () => {
    if (disabled) return;
    setIsDropdownOpen(!isDropdownOpen);
  };

  const selectedTypes = getSelectedFingerTypes();

  return (
    <div className={`finger-toggle-container ${className}`} ref={dropdownRef}>
      <button
        onClick={handleToggleClick}
        title={disabled ? 'Configuration locked' : 'Select finger types'}
        className={`finger-toggle ${disabled ? 'disabled' : ''}`}
        disabled={disabled}
      >
        <span className="finger-text">{getDisplayText()}</span>
      </button>

      {isDropdownOpen && !disabled && (
        <div className="finger-dropdown">
          <div className="dropdown-header">
            <span>Select Fingers</span>
          </div>
          <div className="dropdown-options">
            {FINGER_TYPE_OPTIONS.map(option => (
              <label key={option.type} className="dropdown-option">
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(option.type)}
                  onChange={() => handleFingerTypeToggle(option.type)}
                />
                <span className="checkbox-custom"></span>
                <span className="option-label">{option.label}</span>
              </label>
            ))}
          </div>
          <div className="dropdown-footer">
            <button
              onClick={() => {
                const allTypes: FingerTypeCategory[] = ['pinky', 'ring', 'middle', 'pointing', 'thumb'];
                onChange(fingerTypesToFingers(allTypes));
              }}
              className="select-all-btn"
            >
              Select All
            </button>
            <button
              onClick={() => setIsDropdownOpen(false)}
              className="close-btn"
            >
              Done
            </button>
          </div>
        </div>
      )}

      <style>{`
        .finger-toggle-container {
          position: relative;
          display: inline-block;
        }

        .finger-toggle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 3px solid white;
          background: rgba(17, 24, 39, 0.8);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease-out;
          backdrop-filter: blur(10px);
          font-weight: 600;
          font-size: 0.75rem;
        }

        .finger-toggle:hover {
          transform: scale(1.05);
          background: rgba(59, 130, 246, 0.3);
          border-color: #3b82f6;
        }

        .finger-toggle:active {
          transform: scale(0.95);
        }

        .finger-toggle.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: rgba(75, 85, 99, 0.3);
          border-color: rgba(75, 85, 99, 0.5);
        }

        .finger-toggle.disabled:hover {
          transform: none;
          background: rgba(75, 85, 99, 0.3);
          border-color: rgba(75, 85, 99, 0.5);
        }

        .finger-text {
          font-family: 'Courier New', monospace;
          font-size: 0.7rem;
          font-weight: 700;
          line-height: 1;
          max-width: 32px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .finger-dropdown {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 8px;
          background: rgba(17, 24, 39, 0.95);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 12px;
          backdrop-filter: blur(15px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
          z-index: 1000;
          min-width: 200px;
          animation: dropdownAppear 0.2s ease-out;
        }

        @keyframes dropdownAppear {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0) scale(1);
          }
        }

        .dropdown-header {
          padding: 12px 16px;
          border-bottom: 1px solid rgba(75, 85, 99, 0.3);
          font-size: 0.875rem;
          font-weight: 600;
          color: #d1d5db;
          text-align: center;
        }

        .dropdown-options {
          padding: 8px 0;
        }

        .dropdown-option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.15s ease-out;
          user-select: none;
        }

        .dropdown-option:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .dropdown-option input[type="checkbox"] {
          display: none;
        }

        .checkbox-custom {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(156, 163, 175, 0.6);
          border-radius: 3px;
          position: relative;
          background: transparent;
          transition: all 0.2s ease-out;
        }

        .dropdown-option input[type="checkbox"]:checked + .checkbox-custom {
          background: #3b82f6;
          border-color: #3b82f6;
        }

        .dropdown-option input[type="checkbox"]:checked + .checkbox-custom::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 12px;
          font-weight: bold;
        }

        .option-label {
          font-size: 0.875rem;
          color: #e5e7eb;
        }

        .dropdown-footer {
          display: flex;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid rgba(75, 85, 99, 0.3);
        }

        .select-all-btn, .close-btn {
          flex: 1;
          padding: 6px 12px;
          border: none;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease-out;
        }

        .select-all-btn {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .select-all-btn:hover {
          background: rgba(59, 130, 246, 0.3);
        }

        .close-btn {
          background: rgba(75, 85, 99, 0.3);
          color: #d1d5db;
          border: 1px solid rgba(75, 85, 99, 0.5);
        }

        .close-btn:hover {
          background: rgba(75, 85, 99, 0.5);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .finger-toggle {
            width: 36px;
            height: 36px;
            border-width: 2px;
          }
          
          .finger-text {
            font-size: 0.625rem;
          }

          .finger-dropdown {
            min-width: 180px;
          }
        }
      `}</style>
    </div>
  );
};

export default FingerToggle;
